import { collection, doc, getDoc, getDocs, query, updateDoc, where } from "firebase/firestore";
import { db } from "../../../../firebase";
import { useEffect, useState } from "react";
import { watchFields } from "../../../../utils/fields";
import AdminButton from "../../../core/admin/adminButton";
import AdminToast from "../../../core/admin/adminToast";
import AdminPage from "../../../core/admin/adminPage";
import AdminCard from "../../../core/admin/adminCard";
import AdminTable from "../../../core/admin/adminTable";
import AdminTextInput from "../../../core/admin/adminTextInput";
import AdminEmptyState from "../../../core/admin/adminEmptyState";
import AdminBadge, { AdminCount } from "../../../core/admin/adminBadge";
import { AdminSelect } from "../../../core/admin/adminField";
import { AdminLoading } from "../../../core/admin/adminSpinner";
import { ImageIcon, SearchLineIcon, SortIcon } from "../../../../img/svg/adminIcons";
import CandidateWatchModal from "./candidateWatchModal";
import { IWatch } from "../watches/adminWatches";
import DisplayedBanner from "./displayedBanner";

export default function AdminBanners() {

    const [banners, setBanners] = useState<{ [key: string]: any }[]>([]);
    const [watches, setWatches] = useState<IWatch[]>([]);
    const [filterName, setFilterName] = useState("");
    const [filterValue, setFilterValue] = useState("");

    const [candidateModal, setCandidateModal] = useState(false);
    const [candidateWatch, setCandidateWatch] = useState<IWatch | undefined>(undefined);

    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    const [reorder, setReorder] = useState(false);

    const [badToast, setBadToast] = useState(false);
    const [badToastMessage, setBadToastMessage] = useState("");

    useEffect(() => {
        getBanners();
    }, [])

    const doRefresh = async () => {
        await getBanners();
        await getCandidateWatches(filterName, filterValue);
    }

    const handleBanner = async (watch: IWatch) => {
        try {
            const docRef = doc(db, "watches", watch.id);
            if (watch.isBanner) {
                await updateDoc(docRef, { isBanner: false, order: 0 });
                return true;
            }
            // só relógio em exibição pode virar banner (a home também só mostra esses)
            if (watch.status !== "display") {
                return null;
            }
            await updateDoc(docRef, { isBanner: true, order: 0 });
            return true;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    const getBanners = async () => {
        try {
            setLoading(true);
            const documents: { [key: string]: any }[] = [];
            // mesma consulta da home: um isBanner esquecido num relógio fora do ar não conta como banner
            const q = query(collection(db, "watches"), where("status", "==", "display"), where("isBanner", "==", true));
            const docs = await getDocs(q);
            if (docs.empty) {
                setBanners(documents);
                setLoading(false);
                return null;
            }
            docs.docs.forEach(b => {
                documents.push({
                    id: b.id,
                    brand: b.data().brand,
                    model: b.data().model,
                    year: b.data().year,
                    price: b.data().price,
                    slug: b.data().slug,
                    imgUrl: b.data().images?.length ? b.data().images[0].cImgUrl : undefined,
                    serial: b.data().serial,
                    order: b.data().order
                });
            })
            setBanners(documents.sort((a, b) => a.order - b.order));
            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.log(error);
        }
    }

    const showBadToast = (message: string) => {
        setBadToastMessage(message);
        setBadToast(true);
    }

    const getCandidateWatches = async (filter: string = "", value: string = "") => {
        try {
            const docs: IWatch[] = [];
            let q;
            if (filter === "id") {
                const docum = doc(db, "watches", value);
                const docSnap = await getDoc(docum);
                if (!docSnap.exists()) {
                    setWatches([]);
                    showBadToast("Nenhum relógio encontrado");
                    return null;
                }
                // a busca por ID também se restringe a relógios em exibição, como os demais filtros
                if (docSnap.data().status !== "display") {
                    setWatches([]);
                    showBadToast("Este relógio não está em exibição e não pode virar banner");
                    return null;
                }
                setWatches([{ id: docSnap.id, ...docSnap.data() }]);
                return null;
            }
            if (filter && value) {
                if (filter === "brand" || filter === "model") {
                    q = query(collection(db, "watches"), where("status", "==", "display"));
                    const docsSnap = await getDocs(q);
                    if (docsSnap.empty) {
                        setWatches([]);
                        showBadToast("Nenhum relógio encontrado");
                        return null;
                    }
                    docsSnap.forEach(document => {
                        docs.push({
                            id: document.id,
                            ...document.data()
                        })
                    })
                    return setWatches(docs.filter(d => d.brand.toLowerCase().includes(value.toLowerCase()) || d.model.toLowerCase().includes(value.toLowerCase())));
                }
                q = query(collection(db, "watches"), where("status", "==", "display"), where(filter, "==", filter === "year" ? parseInt(value) : filter === "serial" ? value : value.toLowerCase()));

            } else {
                q = query(collection(db, "watches"), where("status", "==", "display"));
            }
            const docsSnap = await getDocs(q);
            if (docsSnap.empty) {
                setWatches([]);
                showBadToast("Nenhum relógio encontrado");
                return null;
            }
            docsSnap.forEach(document => {
                docs.push({
                    id: document.id,
                    ...document.data()
                })
            })
            setWatches(docs);
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    const doSearch = async () => {
        setSearching(true);
        await getCandidateWatches(filterName, filterValue);
        setSearching(false);
    }

    const handleCandidateModal = (watch: IWatch) => {
        setCandidateWatch(watch);
        setCandidateModal(true);
    }

    const bannerIds = banners.map(b => b.id);

    return (
        <AdminPage title="Banners" description="Relógios em destaque no carrossel da página inicial. Apenas relógios em exibição podem virar banner.">

            {/* EM EXIBIÇÃO */}
            <AdminCard
                title={<>Banners em exibição {loading ? null : <AdminCount count={banners.length} tone={banners.length ? "gold" : "neutral"} />}</>}
                description={reorder ? "Digite a posição de cada banner (menor aparece primeiro) e salve individualmente." : undefined}
                actions={
                    banners.length ?
                        reorder ?
                            <AdminButton variant="secondary" onClick={() => setReorder(false)}>Concluir</AdminButton>
                            : <AdminButton variant="secondary" icon={<SortIcon width="16px" />} onClick={() => setReorder(true)}>Reordenar</AdminButton>
                        : null
                }
                bodyClassName="p-3 md:p-4"
            >
                {
                    loading ?
                        <AdminLoading />
                        : banners.length ?
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                                {
                                    banners.map(b => (
                                        <DisplayedBanner key={b.id} banner={b} reorder={reorder} refresh={getBanners} />
                                    ))
                                }
                            </div>
                            :
                            <AdminEmptyState icon={<ImageIcon />} title="Nenhum banner em exibição" description="Procure um relógio abaixo e use “Tornar banner”." />
                }
            </AdminCard>

            {/* PROCURAR */}
            <AdminCard title="Procurar relógios" description="Sem filtro, lista todos os relógios em exibição.">
                <div className="flex flex-col sm:flex-row gap-2 max-w-2xl">
                    <AdminSelect value={filterName} onChange={e => setFilterName(e.target.value)} className="sm:w-44">
                        <option value="">Sem filtro</option>
                        {
                            Object.keys(watchFields).map((k, i) => (
                                <option key={i} value={k}>{watchFields[k]}</option>
                            ))
                        }
                    </AdminSelect>
                    <AdminTextInput value={filterValue} onChange={e => setFilterValue(e.target.value)} onEnter={doSearch} placeholder={filterName ? `Valor para ${String(watchFields[filterName]).toLowerCase()}` : "Selecione um filtro (opcional)"} />
                    <AdminButton icon={<SearchLineIcon width="16px" />} loading={searching} onClick={() => doSearch()}>Pesquisar</AdminButton>
                </div>

                {
                    watches.length ?
                        <div className="mt-5">
                            <AdminTable
                                rows={watches}
                                rowKey={(w, i) => w.id || i}
                                columns={[
                                    { header: "#", mobile: "hidden", className: "w-10 text-gray-400 tabular-nums", render: (w, i) => i + 1 },
                                    { header: "Relógio", mobile: "title", render: w => <span className="font-semibold">{w.brand} {w.model}</span> },
                                    { header: "Ano", render: w => w.year },
                                    { header: "Banner", mobile: "aside", render: w => bannerIds.includes(w.id) ? <AdminBadge tone="gold" dot>Banner</AdminBadge> : <AdminBadge tone="neutral">Não</AdminBadge> },
                                ]}
                                actions={w => <AdminButton size="sm" variant="secondary" onClick={() => handleCandidateModal(w)}>Ver</AdminButton>}
                            />
                        </div>
                        : null
                }
            </AdminCard>
            {
                candidateModal && candidateWatch ?
                    <CandidateWatchModal watch={candidateWatch} close={() => setCandidateModal(false)} handleBanner={handleBanner} banners={bannerIds} refresh={doRefresh} />
                    : null
            }
            <AdminToast toast={badToast} setToast={setBadToast} message={badToastMessage} tone="danger" />
        </AdminPage>
    )
}
